import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Tag,
  Tooltip,
  Spin,
  Button,
  Dropdown,
  Modal,
  notification,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { Link } from "react-router-dom";
import { ValidationRun } from "../../../model_gen/finetuning";
import {
  MoreOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { ValidationService } from "../../../services/ValidationService";
import { usePermissions } from "../../../PermissionsProvider";

type ValidationRunColumns = ColumnsType<ValidationRun>;

interface ValidationRunTableProps {
  validationRuns: ValidationRun[];
  isLoading: boolean;
  showCompany?: boolean;
  onRefresh?: () => void;
  pagination?: {
    current?: number;
    pageSize?: number;
    total?: number;
    onChange?: (page: number, pageSize: number) => void;
  };
}

const ValidationRunTable: React.FC<ValidationRunTableProps> = ({
  validationRuns,
  isLoading,
  showCompany = false,
  onRefresh,
  pagination,
}) => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const getStatusTagColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case "COMPLETED":
        return "success";
      case "RUNNING":
        return "processing";
      case "FAILED":
        return "error";
      case "CANCELLED":
        return "warning";
      default:
        return "default";
    }
  };

  const getAccuracyTagColor = (accuracy: number): string => {
    if (accuracy >= 0.9) return "success";
    if (accuracy >= 0.7) return "processing";
    if (accuracy >= 0.5) return "warning";
    return "error";
  };

  const handleDeleteRun = (
    run: ValidationRun,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    Modal.confirm({
      title: "Delete Validation Run",
      icon: <ExclamationCircleOutlined />,
      content:
        "Are you sure you want to delete this validation run? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        ValidationService.deleteValidationRun(run, null, apiKey).subscribe(
          () => {
            notification.success({
              message: "Success",
              description: "Validation run deleted successfully",
            });
            onRefresh?.();
          },
          (error: any) => {
            console.error(error);
            notification.error({
              message: "Error",
              description: "Failed to delete validation run",
            });
          }
        );
      },
    });
  };

  const baseColumns: ValidationRunColumns = [
    {
      title: "Finetune Run",
      key: "finetune_run_link",
      width: 250,
      render: (_, record) => (
        <Link
          to={`/build/finetuning/${record.finetune_run.id}`}
          onClick={(e) => e.stopPropagation()}>
          {record.finetune_run.display_name}
        </Link>
      ),
    },
    {
      title: "Model",
      key: "model",
      dataIndex: "model",
      width: 200,
      render: (model) => {
        return model.replace("Meta-", "").toLowerCase();
      },
    },
    {
      title: "Accuracy",
      key: "accuracy",
      dataIndex: "accuracy",
      width: 120,
      render: (accuracy, record) => {
        const accuracyPercent =
          accuracy != null ? (accuracy * 100).toFixed(2) : "N/A";
        const tagColor = getAccuracyTagColor(accuracy);
        return (
          <Tooltip
            title={`${record.correct_requests} / ${record.total_requests}`}>
            <Tag color={tagColor}>{accuracyPercent}%</Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.accuracy || 0) - (b.accuracy || 0),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: 200,
      render: (date) => moment(date).format("YYYY-MM-DD hh:mm:ss A"),
      sorter: (a, b) =>
        moment(a.date_created).unix() - moment(b.date_created).unix(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status) => {
        const statusUpper = status.toUpperCase();
        return <Tag color={getStatusTagColor(status)}>{statusUpper}</Tag>;
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "",
      key: "actions",
      width: "48px",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) =>
                  handleDeleteRun(
                    record,
                    e.domEvent as React.MouseEvent<HTMLButtonElement>
                  ),
              },
            ],
          }}
          trigger={["click"]}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  const columns = showCompany
    ? [
        {
          title: "Company",
          key: "company_name",
          dataIndex: "company_name",
          width: 100,
        },
        ...baseColumns,
      ]
    : baseColumns;

  if (isLoading) {
    return (
      <Spin
        size="large"
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      />
    );
  }

  return (
    <Table
      rowKey={(record) => record.id.toString()}
      columns={columns}
      dataSource={validationRuns}
      pagination={
        pagination || {
          pageSize: 10,
          showSizeChanger: false,
        }
      }
      onRow={(record) => ({
        onClick: () =>
          navigate(
            `/build/finetuning/${record.finetune_run.id}/validation/${record.id}`
          ),
      })}
    />
  );
};

export default ValidationRunTable;
